import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaMapMarkerAlt,
  FaCalculator,
} from "react-icons/fa";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import { useGoogleMaps } from "../../context/GoogleMapsContext";

// Helper to get full endpoint and token header
const buildConfig = (token) => ({
  baseURL: BASE_API_URL,
  headers: token ? { Authorization: `Bearer ${token}` } : {},
});

const endpoint = "/popular-routes";

const PopularRouteManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const { isLoaded: isGoogleLoaded, calculateDistance } = useGoogleMaps();
  const [routes, setRoutes] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const [formData, setFormData] = useState({
    from: "",
    to: "",
    distance: "",
    duration: "",
    sequence: 1,
    cabTypePrices: {},
  });
  const [coordinates, setCoordinates] = useState({
    origin: null,
    destination: null,
  });

  // Get auth token
  const getAuthToken = () => {
    return user?.token;
  };

  // Get autocomplete suggestions from Google Places
  const getPlaceSuggestions = async (input, setterFunction) => {
    if (!input || input.length < 2 || !isGoogleLoaded) return;

    try {
      const service = new window.google.maps.places.AutocompleteService();
      const request = {
        input: input,
        componentRestrictions: { country: ['in'] }, // Restrict to India
        types: ['geocode'], // Only show geographical locations
      };

      const predictions = await new Promise((resolve) => {
        service.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions || []);
          } else {
            resolve([]);
          }
        });
      });

      setterFunction(predictions);
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      setterFunction([]);
    }
  };

  // Calculate distance and duration from Google Maps API
  const calculateDistanceAndDuration = async () => {
    if (!formData.from.trim() || !formData.to.trim()) {
      toast.error("Please enter both 'From' and 'To' locations");
      return;
    }

    setIsCalculating(true);
    try {
      console.log("🚀 Sending distance calculation request:");
      console.log(`  From: "${formData.from.trim()}"`);
      console.log(`  To: "${formData.to.trim()}"`);

      const response = await axios.get(`${BASE_API_URL}/distance`, {
        params: {
          origin: formData.from.trim(),
          destination: formData.to.trim(),
        },
      });

      console.log("📡 Distance API Response:", response.data);

      if (response.data.success && response.data.data) {
        const { distanceText, durationText, origin, destination } = response.data.data;

        // Store coordinates for display
        setCoordinates({
          origin: origin,
          destination: destination,
        });

        // Log coordinates for developer reference
        console.log("📍 Route Coordinates Received:", {
          origin: {
            name: origin?.name,
            latitude: origin?.lat,
            longitude: origin?.lng,
            source: origin?.source,
          },
          destination: {
            name: destination?.name,
            latitude: destination?.lat,
            longitude: destination?.lng,
            source: destination?.source,
          },
          distance: distanceText,
          duration: durationText,
          apiSource: response.data.source,
        });

        setFormData((prev) => ({
          ...prev,
          distance: distanceText || "",
          duration: durationText || "",
        }));
        toast.success("Distance and duration calculated successfully!");
      } else {
        console.error("❌ Distance calculation failed - no data in response:", response.data);
        toast.error("Could not calculate distance. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error calculating distance:", error);
      console.error("   Status:", error.response?.status);
      console.error("   Message:", error.response?.data?.message);
      console.error("   Full error:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to calculate distance");
    } finally {
      setIsCalculating(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion, isFromField) => {
    const description = suggestion.description;
    if (isFromField) {
      setFormData((prev) => ({ ...prev, from: description }));
      setFromSuggestions([]);
      setShowFromSuggestions(false);
    } else {
      setFormData((prev) => ({ ...prev, to: description }));
      setToSuggestions([]);
      setShowToSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromInputRef.current && !fromInputRef.current.contains(event.target)) {
        setShowFromSuggestions(false);
      }
      if (toInputRef.current && !toInputRef.current.contains(event.target)) {
        setShowToSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch routes from API
  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      setIsLoading(true);
      const config = buildConfig(getAuthToken());
      const [routesResponse, cabTypesResponse] = await Promise.all([
        axios.get(`${endpoint}`, config),
        axios.get(`${BASE_API_URL}/cab`, config),
      ]);

      // Support both array returns and { data: [...] } shapes
      const routesData = Array.isArray(routesResponse.data)
        ? routesResponse.data
        : routesResponse.data?.data || [];

      const cabTypesData = cabTypesResponse.data?.data || cabTypesResponse.data || [];

      setRoutes(routesData);
      setCabTypes(cabTypesData);
    } catch (error) {
      console.error("Error fetching data:", error, error?.response?.data);
      const message = error?.response?.data?.message || error.message || "Failed to load data";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (route = null) => {
    if (route) {
      // Convert cabTypePrices array to object for easier form handling
      const cabTypePricesObj = {};
      if (route.cabTypePrices) {
        route.cabTypePrices.forEach((ctp) => {
          const cabTypeId = ctp.cabTypeId && (ctp.cabTypeId._id || ctp.cabTypeId) ? (ctp.cabTypeId._id || ctp.cabTypeId) : null;
          if (cabTypeId) cabTypePricesObj[cabTypeId] = ctp.price;
        });
      }

      setFormData({
        from: route.from || "",
        to: route.to || "",
        distance: route.distance || "",
        duration: route.duration || "",
        sequence: route.sequence || 1,
        cabTypePrices: cabTypePricesObj,
      });

      // Load existing coordinates if available
      setCoordinates({
        origin: route.fromLat && route.fromLng ? {
          name: route.from,
          lat: route.fromLat,
          lng: route.fromLng,
        } : null,
        destination: route.toLat && route.toLng ? {
          name: route.to,
          lat: route.toLat,
          lng: route.toLng,
        } : null,
      });

      setCurrentRoute(route);
    } else {
      // Initialize cab type prices for new route
      const cabTypePricesObj = {};
      cabTypes.forEach((cabType) => {
        cabTypePricesObj[cabType._id] = 0;
      });

      setFormData({
        from: "",
        to: "",
        distance: "",
        duration: "",
        sequence: routes.length + 1,
        cabTypePrices: cabTypePricesObj,
      });
      setCurrentRoute(null);
      setCoordinates({
        origin: null,
        destination: null,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentRoute(null);
    setFormData({
      from: "",
      to: "",
      distance: "",
      duration: "",
      sequence: 1,
      cabTypePrices: {},
    });
    setCoordinates({
      origin: null,
      destination: null,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Trigger autocomplete suggestions
    if (name === "from" && isGoogleLoaded) {
      getPlaceSuggestions(value, setFromSuggestions);
      setShowFromSuggestions(value.length >= 2);
    } else if (name === "to" && isGoogleLoaded) {
      getPlaceSuggestions(value, setToSuggestions);
      setShowToSuggestions(value.length >= 2);
    }
  };

  const handleCabTypePriceChange = (cabTypeId, price) => {
    setFormData((prev) => ({
      ...prev,
      cabTypePrices: {
        ...prev.cabTypePrices,
        [cabTypeId]: parseInt(price) || 0,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.from.trim() ||
      !formData.to.trim() ||
      !formData.sequence
    ) {
      toast.error(
        "All fields (From, To, Sequence) are required!",
      );
      return;
    }

    if (!formData.distance.trim() || !formData.duration.trim()) {
      toast.error(
        "Please click 'Auto-Calculate Distance' to fill in distance and duration from Google Maps!",
      );
      return;
    }

    // Validate that coordinates are available
    if (!coordinates.origin || !coordinates.destination) {
      toast.error(
        "Please click 'Auto-Calculate Distance' to get location coordinates!",
      );
      return;
    }

    // Validate that at least one cab type has a price set
    const hasValidPrices = Object.values(formData.cabTypePrices).some(
      (price) => price > 0,
    );
    if (!hasValidPrices) {
      toast.error("Please set prices for at least one cab type!");
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        toast.error("Not authenticated. Please log in as admin to perform this action.");
        return;
      }

      // Convert cab type prices object to array format for backend
      const cabTypePricesArray = Object.entries(formData.cabTypePrices).map(
        ([cabTypeId, price]) => ({
          cabTypeId,
          price: Number(price) || 0,
        }),
      );

      const routeData = {
        from: formData.from.trim(),
        to: formData.to.trim(),
        distance: formData.distance.trim(),
        duration: formData.duration.trim(),
        sequence: Number(formData.sequence) || 1,
        cabTypePrices: cabTypePricesArray,
        fromLat: Number(coordinates.origin.lat),
        fromLng: Number(coordinates.origin.lng),
        toLat: Number(coordinates.destination.lat),
        toLng: Number(coordinates.destination.lng),
      };

      const config = buildConfig(token);

      if (currentRoute) {
        // Update existing route
        const res = await axios.put(`${endpoint}/${currentRoute._id}`, routeData, config);
        toast.success(res.data?.message || "Route updated successfully!");
      } else {
        // Add new route
        const res = await axios.post(`${endpoint}`, routeData, config);
        toast.success(res.data?.message || "Route added successfully!");
      }

      handleCloseModal();
      fetchRoutes();
    } catch (error) {
      console.error("Error saving route:", error, error?.response?.data);
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message || JSON.stringify(error?.response?.data) || error.message;
      if (status === 401) {
        toast.error("Unauthorized: Please log in as admin.");
      } else if (status === 400) {
        toast.error(serverMessage);
      } else {
        toast.error(serverMessage || "Failed to save route");
      }
    }
  };

  const handleToggleStatus = async (routeId, currentStatus) => {
    try {
      const token = getAuthToken();
      if (!token) return toast.error("Not authenticated");
      const config = buildConfig(token);
      const res = await axios.put(`${endpoint}/${routeId}`, { isActive: !currentStatus }, config);
      toast.success(res.data?.message || "Route status updated successfully!");
      fetchRoutes();
    } catch (error) {
      console.error("Error updating route status:", error, error?.response?.data);
      const serverMessage = error?.response?.data?.message || error.message;
      toast.error(serverMessage || "Failed to update route status");
    }
  };

  const handleDelete = async (routeId, routeName) => {
    if (window.confirm(`Are you sure you want to delete "${routeName}"?`)) {
      try {
        const token = getAuthToken();
        if (!token) return toast.error("Not authenticated");
        const config = buildConfig(token);
        const res = await axios.delete(`${endpoint}/${routeId}`, config);
        toast.success(res.data?.message || "Route deleted successfully!");
        fetchRoutes();
      } catch (error) {
        console.error("Error deleting route:", error, error?.response?.data);
        const serverMessage = error?.response?.data?.message || error.message;
        toast.error(serverMessage || "Failed to delete route");
      }
    }
  };

  const filteredRoutes = routes
    .filter(
      (route) =>
        route.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        route.to.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Fixed Routes Management
          </h1>
          <p className="text-gray-600">
            Manage featured routes displayed on the homepage
          </p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search routes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaPlus /> Add Route
            </button>
          </div>
        </div>

        {/* Routes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading routes...</p>
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No routes found
            </div>
          ) : (
            filteredRoutes.map((route) => (
              <div
                key={route._id}
                className="bg-white rounded-lg shadow-sm border"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                          #{route.sequence || 0}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {route.from} → {route.to}
                        </h3>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        handleToggleStatus(route._id, route.isActive)
                      }
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        route.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {route.isActive ? <FaToggleOn /> : <FaToggleOff />}
                      {route.isActive ? "Active" : "Inactive"}
                    </button>
                  </div>

                  <div className="space-y-1 text-sm text-gray-600 mb-4">
                    <p>
                      <span className="font-medium">Distance:</span>{" "}
                      {route.distance}
                    </p>
                    <p>
                      <span className="font-medium">Duration:</span>{" "}
                      {route.duration}
                    </p>
                  </div>

                  {/* Cab Type Prices */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Cab Type Prices:
                    </p>
                    <div className="space-y-2">
                      {route.cabTypePrices && route.cabTypePrices.length > 0 ? (
                        route.cabTypePrices.map((ctp) => {
                          const cabType = ctp.cabTypeId && (ctp.cabTypeId.name || (ctp.cabTypeId && ctp.cabTypeId._id)) ? (ctp.cabTypeId.name ? ctp.cabTypeId : { name: ctp.cabTypeId, _id: ctp.cabTypeId }) : { name: 'Unknown Cab Type', _id: ctp.cabTypeId };
                          const cabTypeId = cabType._id || cabType;
                          return (
                            <div
                              key={cabTypeId}
                              className="flex justify-between items-center bg-gray-50 p-2 rounded"
                            >
                              <span className="text-sm text-gray-800">
                                {cabType.name || "Unknown Cab Type"}
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                ₹{ctp.price}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="text-xs text-gray-500">
                          No prices set
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenModal(route)}
                      className="flex-1 flex items-center justify-center gap-1 bg-blue-100 text-blue-700 py-2 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(route._id, `${route.from} to ${route.to}`)
                      }
                      className="flex-1 flex items-center justify-center gap-1 bg-red-100 text-red-700 py-2 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  {currentRoute ? "Edit Route" : "Add New Route"}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* From and To with Google Places Autocomplete */}
                  <div className="grid grid-cols-2 gap-4">
                    <div ref={fromInputRef} className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From * <FaMapMarkerAlt className="inline text-blue-500 ml-1" />
                      </label>
                      <input
                        type="text"
                        name="from"
                        value={formData.from}
                        onChange={handleChange}
                        onFocus={() => setShowFromSuggestions(formData.from.length >= 2)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search location..."
                        required
                        disabled={!isGoogleLoaded}
                      />
                      {showFromSuggestions && fromSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                          {fromSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleSuggestionClick(suggestion, true)}
                              className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm text-gray-700"
                            >
                              <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      )}
                      {!isGoogleLoaded && (
                        <p className="text-xs text-gray-500 mt-1">Loading Google Places...</p>
                      )}
                    </div>
                    <div ref={toInputRef} className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To * <FaMapMarkerAlt className="inline text-red-500 ml-1" />
                      </label>
                      <input
                        type="text"
                        name="to"
                        value={formData.to}
                        onChange={handleChange}
                        onFocus={() => setShowToSuggestions(formData.to.length >= 2)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search location..."
                        required
                        disabled={!isGoogleLoaded}
                      />
                      {showToSuggestions && toSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                          {toSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              onClick={() => handleSuggestionClick(suggestion, false)}
                              className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm text-gray-700"
                            >
                              <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      )}
                      {!isGoogleLoaded && (
                        <p className="text-xs text-gray-500 mt-1">Loading Google Places...</p>
                      )}
                    </div>
                  </div>

                  {/* Auto-calculate Distance & Duration Button */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={calculateDistanceAndDuration}
                      disabled={isCalculating || !formData.from || !formData.to}
                      className="flex items-center justify-center gap-2 flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <FaCalculator /> {isCalculating ? "Calculating..." : "Auto-Calculate Distance"}
                    </button>
                  </div>

                  {/* Coordinates Display Section */}
                  {(coordinates.origin || coordinates.destination) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-sm font-bold text-blue-900 mb-3">📍 Route Coordinates</h3>
                      <div className="grid grid-cols-1 gap-3">
                        {coordinates.origin && (
                          <div className="bg-white p-2 rounded border border-blue-100">
                            <p className="text-xs font-semibold text-gray-700">From: {coordinates.origin.name}</p>
                            <div className="text-xs text-gray-600 mt-1 space-y-1">
                              <p>Latitude: <span className="font-mono font-semibold">{coordinates.origin.lat?.toFixed(4) || "N/A"}</span></p>
                              <p>Longitude: <span className="font-mono font-semibold">{coordinates.origin.lng?.toFixed(4) || "N/A"}</span></p>
                              {coordinates.origin.source && <p className="text-gray-500">Source: <span className="italic">{coordinates.origin.source}</span></p>}
                            </div>
                          </div>
                        )}
                        {coordinates.destination && (
                          <div className="bg-white p-2 rounded border border-blue-100">
                            <p className="text-xs font-semibold text-gray-700">To: {coordinates.destination.name}</p>
                            <div className="text-xs text-gray-600 mt-1 space-y-1">
                              <p>Latitude: <span className="font-mono font-semibold">{coordinates.destination.lat?.toFixed(4) || "N/A"}</span></p>
                              <p>Longitude: <span className="font-mono font-semibold">{coordinates.destination.lng?.toFixed(4) || "N/A"}</span></p>
                              {coordinates.destination.source && <p className="text-gray-500">Source: <span className="italic">{coordinates.destination.source}</span></p>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Distance and Duration (read-only, auto-filled) */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Distance * (km)
                      </label>
                      <input
                        type="text"
                        name="distance"
                        value={formData.distance}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                        placeholder="Auto-calculated"
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto-calculated from Google Maps</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration * (hours)
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                        placeholder="Auto-calculated"
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto-calculated from Google Maps</p>
                    </div>
                  </div>

                  {/* Sequence */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sequence *
                    </label>
                    <input
                      type="number"
                      name="sequence"
                      value={formData.sequence}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1"
                      min="1"
                      required
                    />
                  </div>

                  {/* Cab Type Pricing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Set Price for Each Cab Type *
                    </label>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {cabTypes.map((cabType) => (
                        <div
                          key={cabType._id}
                          className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg"
                        >
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-800">
                              {cabType.name}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({cabType.acType} - {cabType.seatingCapacity}{" "}
                              seats)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">₹</span>
                            <input
                              type="number"
                              value={formData.cabTypePrices[cabType._id] || 0}
                              onChange={(e) =>
                                handleCabTypePriceChange(
                                  cabType._id,
                                  e.target.value,
                                )
                              }
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                      ))}
                      {cabTypes.length === 0 && (
                        <div className="text-center text-gray-500 py-4">
                          No cab types available
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {currentRoute ? "Update" : "Add"} Route
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PopularRouteManagement;
